/**
 * Bitget の Classic Account (v2) / Unified Account (v3) を自動で切り替える。
 *
 * Bitget はアカウント種別と API バージョンが不一致の場合、署名検証より前に
 * 業務エラーを返す (Classic 側は 40085 / UTA 側は 40084)。
 * どちらの種別かは事前に判定できないため、まず v2 を試し、
 * 種別不一致と判定できた場合だけ v3 へ切り替える。
 */
import { ExchangeApiConfigurationException } from '../errors'

/**
 * 種別不一致による失敗かを判定する。
 * @param params - error: 判定対象の例外。
 * @returns Unified Account 起因で Classic API が使えない場合は true。
 */
const isUnifiedAccountError = ({
  error,
}: {
  readonly error: unknown
}): boolean =>
  error instanceof ExchangeApiConfigurationException &&
  error.reason === 'bitget-unified-account'

/**
 * Classic API (v2) を試し、Unified Account 起因で失敗した場合だけ UTA API (v3) を実行する。
 * v2 が成功した場合や、種別不一致以外の失敗では v3 を呼ばない。
 * @param params - classic: v2 実行関数、unified: v3 実行関数。
 * @returns 実行できた側の結果。
 */
export const runWithAccountModeFallback = async <T>({
  classic,
  unified,
}: {
  readonly classic: () => Promise<T>
  readonly unified: () => Promise<T>
}): Promise<T> => {
  try {
    return await classic()
  } catch (error) {
    if (!isUnifiedAccountError({ error })) {
      throw error
    }
  }
  return await unified()
}
